import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IFileAttachment, SocialMediaPostViewModel } from '../../../../extViewmodels';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../models';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { useBrandingSettingsQuery } from '../../../../queries';
import { hasBrandingSettings } from '../../../../utils/hasBrandingSettings';
import { AddMediaEmptyState } from '../../../components-v2/AddMediaEmptyState';
import { UnifiedImageDialog } from '../../../components-v2/UnifiedImageDialog';
import { CropOptions, TabValue } from '../../../components-v2/UnifiedImageDialog/types';
import { useEditSocialMediaPostContext } from '../../../containers/socialMedia/EditSocialMediaPost/context';
import { IMediaEntry, getMediaEntry, removeMediaEntry, replaceMediaEntry } from './mediaEntries';
import { MediaGrid } from './MediaGrid';
import {
	getCropBlockedReason,
	getLogoOverlayBlockedReason,
	getRebrandBlockedReason,
	isVideoAttachment,
} from './mediaRules';
import { styleSheet } from './styles';

/** The media actions the composer drives from the version bar above the panel. */
export interface IMediaActions {
	openBulkRebranding: () => void;
}

interface IProps extends IImpersonationContextComponentProps {
	/** Filled with the actions the composer renders outside this component. */
	actionsRef?: React.Ref<IMediaActions>;
	/** The media on screen: the post's, or the version's named by `platform`. */
	attachments: IFileAttachment[];
	/** Heading for the whole media section. */
	label: string;
	onPostScheduled?: (postViewModel: SocialMediaPostViewModel) => void;
	onValidateSelectedFile?: (file: File) => boolean;
	/** The version on screen while the post is split. Every change here reaches that version alone. */
	platform?: Api.SocialMediaType;
	/** Everything the post publishes on any platform, which the logo overlay, one setting for all, reads. */
	publishedAttachments?: IFileAttachment[];
	post?: SocialMediaPostViewModel;
	readOnly?: boolean;
	styles?: StyleDeclarationValue[];
}

export const MAX_IMAGES = 10;

/** The dialog tabs that bring new media into the post, rather than changing media already in it. */
const ADDING_TABS: readonly TabValue[] = ['upload', 'image-library', 'ai-generate', 'free-images', 'upload-video'];

/**
 * A post publishes one video on its own or up to MAX_IMAGES images, and every platform rejects the mix.
 * Split, the base post also holds every tab's uploads and the API checks it too, so a video or a full
 * set of uploads on another tab blocks this one as well.
 */
const getAddBlockedReason = (media: readonly IFileAttachment[], baseMedia: readonly IFileAttachment[]) => {
	if (media.some(isVideoAttachment)) {
		return 'A video publishes on its own. Remove it to add other media.';
	}
	if (baseMedia.some(isVideoAttachment)) {
		return 'Another platform has a video, and a post can’t mix video and images. Remove it to add media here.';
	}
	if (media.length >= MAX_IMAGES) {
		return `Only ${MAX_IMAGES} images are supported at this time`;
	}
	return baseMedia.length >= MAX_IMAGES ? `Only ${MAX_IMAGES} images can be added across all platforms` : undefined;
};

/**
 * Checked against the media as it is when the addition lands, not as it was when the dialog opened. The
 * base caps what fits too, since split it holds every tab's uploads.
 */
const appendMedia = (
	media: readonly IFileAttachment[],
	baseMedia: readonly IFileAttachment[],
	additions: IFileAttachment[]
) =>
	getAddBlockedReason(media, baseMedia)
		? media
		: [...media, ...additions].slice(0, MAX_IMAGES - Math.max(baseMedia.length - media.length, 0));

/**
 * The tabs that cannot act, each with its reason: the adding tabs while nothing more fits, and the tabs
 * that change media already here while there is none they can change.
 */
const getDisabledDialogTabs = (reasons: {
	addBlockedReason: string | undefined;
	cropBlockedReason: string | undefined;
	logoOverlayBlockedReason: string | undefined;
	rebrandBlockedReason: string | undefined;
	videoUploadBlockedReason: string | undefined;
}) => {
	const disabledTabsReason: Partial<Record<TabValue, string>> = {};
	if (reasons.addBlockedReason) {
		ADDING_TABS.forEach(tab => {
			disabledTabsReason[tab] = reasons.addBlockedReason;
		});
	} else if (reasons.videoUploadBlockedReason) {
		disabledTabsReason['upload-video'] = reasons.videoUploadBlockedReason;
	}
	if (reasons.rebrandBlockedReason) {
		disabledTabsReason['bulk-rebranding'] = reasons.rebrandBlockedReason;
	}
	if (reasons.cropBlockedReason) {
		disabledTabsReason.crop = reasons.cropBlockedReason;
	}
	if (reasons.logoOverlayBlockedReason) {
		disabledTabsReason['logo-overlay'] = reasons.logoOverlayBlockedReason;
	}
	return { disabledTabs: Object.keys(disabledTabsReason) as TabValue[], disabledTabsReason };
};

const SocialMediaImageSearchAndDisplayBase: React.FC<IProps> = observer(props => {
	const {
		actionsRef,
		attachments,
		label,
		readOnly,
		onPostScheduled,
		onValidateSelectedFile,
		platform,
		post,
		publishedAttachments,
		impersonationContext,
	} = props;
	const {
		addActiveImages,
		cropSources,
		hideWhiteBackground,
		logoSize,
		overlayLogo,
		postImages,
		postContent,
		recordCrop,
		schedulePost,
		selectedCreator,
		setActiveImages,
		setHasChanges,
		setLogoSize,
		setOverlayLogo,
		updatePost,
	} = useEditSocialMediaPostContext();
	const userSession = useUserSession();
	const account = impersonationContext?.account || userSession?.account;
	const { data: brandingSettings } = useBrandingSettingsQuery({ impersonationContext: impersonationContext?.toJs() });
	const [unifiedDialogOpen, setUnifiedDialogOpen] = React.useState(false);
	const [unifiedDialogTab, setUnifiedDialogTab] = React.useState<TabValue>('upload');
	// An AI edit opened from a tile swaps its result in for that one copy rather than adding it.
	const [aiEditTarget, setAiEditTarget] = React.useState<{ entry: IMediaEntry; image: IFileAttachment } | null>(null);
	const [logoOverlayIndex, setLogoOverlayIndex] = React.useState<number | undefined>(undefined);
	const [cropIndex, setCropIndex] = React.useState<number | undefined>(undefined);
	const hasVideo = attachments.some(isVideoAttachment);
	const canChange = !readOnly;
	const baseMedia = platform ? (postImages ?? []) : attachments;
	const addBlockedReason = getAddBlockedReason(attachments, baseMedia);
	const rebrandBlockedReason = getRebrandBlockedReason(attachments, hasBrandingSettings(brandingSettings));

	const onRemoveClicked = React.useCallback(
		(_image: IFileAttachment, index: number) => {
			const entry = getMediaEntry(attachments, index);
			setActiveImages(current => removeMediaEntry(current, entry), platform);
		},
		[attachments, platform, setActiveImages]
	);

	const handleOpenImageModal = React.useCallback((tab: TabValue = 'upload') => {
		setUnifiedDialogTab(tab);
		setAiEditTarget(null);
		setUnifiedDialogOpen(true);
	}, []);

	React.useImperativeHandle(
		actionsRef,
		() => ({
			// Opening on a disabled tab would still show its content, so a blocked rebrand opens nothing.
			openBulkRebranding: () => {
				if (canChange && !rebrandBlockedReason) {
					handleOpenImageModal('bulk-rebranding');
				}
			},
		}),
		[canChange, handleOpenImageModal, rebrandBlockedReason]
	);

	const handleOpenAIEdit = React.useCallback(
		(image: IFileAttachment, index: number) => {
			setUnifiedDialogTab('ai-edit');
			setAiEditTarget({ entry: getMediaEntry(attachments, index), image });
			setUnifiedDialogOpen(true);
		},
		[attachments]
	);

	// Enlarging opens the same tab that owns the logo overlay: one place shows the media at size, and
	// what the logo will do to it.
	const handleEnlarge = React.useCallback((_image: IFileAttachment, index: number) => {
		setUnifiedDialogTab('logo-overlay');
		setAiEditTarget(null);
		setLogoOverlayIndex(index);
		setUnifiedDialogOpen(true);
	}, []);

	const handleOpenCrop = React.useCallback((_image: IFileAttachment, index: number) => {
		setUnifiedDialogTab('crop');
		setAiEditTarget(null);
		setCropIndex(index);
		setUnifiedDialogOpen(true);
	}, []);

	const handleUnifiedImageSelected = React.useCallback(
		(image: IFileAttachment) => {
			if (aiEditTarget) {
				setActiveImages(current => replaceMediaEntry(current, aiEditTarget.entry, image), platform);
				return;
			}
			addActiveImages((current, base) => appendMedia(current, base, [image]), platform);
		},
		[addActiveImages, aiEditTarget, platform, setActiveImages]
	);

	const handleMultipleImagesSelected = React.useCallback(
		(images: Api.IFileAttachment[]) => {
			addActiveImages((current, base) => appendMedia(current, base, images), platform);
		},
		[addActiveImages, platform]
	);

	// The crop tab names a copy by its position in `attachments` as rendered with it, which this callback
	// shares, so the copy is worked out here before any change to the media can move it.
	const handleCropped = React.useCallback<CropOptions['onCropped']>(
		({ cropped, framing, index, original }) => {
			const entry = getMediaEntry(attachments, index);
			setActiveImages(current => replaceMediaEntry(current, entry, cropped), platform);
			recordCrop(original, cropped, framing);
			setUnifiedDialogOpen(false);
		},
		[attachments, platform, recordCrop, setActiveImages]
	);

	const handleUndoCrop = React.useCallback(
		(index: number) => {
			const source = cropSources?.[attachments[index]?.id];
			if (!source) {
				return;
			}
			const entry = getMediaEntry(attachments, index);
			setActiveImages(current => replaceMediaEntry(current, entry, source.original), platform);
			setUnifiedDialogOpen(false);
		},
		[attachments, cropSources, platform, setActiveImages]
	);

	const handleBulkRebrandingImagesSelected = (branded: Api.IFileAttachment[]) => {
		setUnifiedDialogOpen(false);
		setActiveImages(() => branded, platform);
	};

	const handleVideoAttachmentGenerated = React.useCallback(
		(attachment: IFileAttachment) => {
			addActiveImages((current, base) => appendMedia(current, base, [attachment]), platform);
			setUnifiedDialogOpen(false);
		},
		[addActiveImages, platform]
	);

	const getPostIdForVideoUpload = React.useCallback(async (): Promise<string> => {
		if (post?.id) {
			await updatePost(post);
			return post.id;
		}
		const [postViewModel] = await schedulePost(null, null, impersonationContext);
		onPostScheduled?.(postViewModel);
		return postViewModel.id;
	}, [onPostScheduled, post, schedulePost, updatePost, impersonationContext]);

	const videoForUserId = React.useMemo(() => {
		if (impersonationContext?.isValid && !selectedCreator) {
			return undefined;
		}

		if (impersonationContext?.isValid) {
			return selectedCreator?.id;
		}

		if (selectedCreator?.id && selectedCreator.id !== userSession?.user?.id) {
			return selectedCreator.id;
		}

		return undefined;
	}, [impersonationContext?.isValid, selectedCreator, userSession?.user?.id]);

	const getVideoUploadBlockedReason = () => {
		if (impersonationContext?.isValid && !videoForUserId) {
			return 'You must select a creator to upload a video';
		}
		// The API converts an uploaded video on the base post and refuses once it holds media, and a split
		// post's base holds every tab's uploads.
		if (platform && baseMedia.length) {
			return 'To add a video, remove the media from every platform first';
		}
		return undefined;
	};
	const disabledDialogTabs = getDisabledDialogTabs({
		addBlockedReason,
		cropBlockedReason: getCropBlockedReason(attachments),
		logoOverlayBlockedReason: getLogoOverlayBlockedReason(attachments),
		rebrandBlockedReason,
		videoUploadBlockedReason: getVideoUploadBlockedReason(),
	});

	// The overlay is burned in at publish time, so it is settled only while no published image already
	// carries one, on any platform: it is one setting for the whole post. Offered from here because this
	// dialog is where the media it lands on gets chosen.
	const isLogoBurnedIn = (publishedAttachments ?? attachments).some(image => image.hasLogoOverlay);
	const logoOverlay = {
		hideWhiteBackground,
		images: attachments,
		initialIndex: logoOverlayIndex,
		isBurnedIn: isLogoBurnedIn,
		isEnabled: !!overlayLogo,
		logoUrl: account?.preferences?.logo?.url,
		onEnabledChange: (isEnabled: boolean) => {
			setOverlayLogo(isEnabled);
			setHasChanges(true);
		},
		onSizeChange: setLogoSize,
		readOnly: !canChange,
		size: logoSize ?? Api.DEFAULT_LOGO_SIZE,
	};

	const crop: CropOptions | undefined = canChange
		? {
				cropSources,
				images: attachments,
				initialIndex: cropIndex,
				onCropped: handleCropped,
				onUndoCrop: handleUndoCrop,
			}
		: undefined;

	const hasMedia = attachments.length > 0;
	const offersAdd = canChange && !hasVideo;
	// Split, another tab's video or uploads can block this tab, which its own media does not explain. An
	// empty tab then shows the disabled add tile and the reason, not an empty state whose button would
	// open a dialog that can only drop what is chosen.
	const isBlockedByOtherTabs = canChange && !!addBlockedReason && !hasVideo && attachments.length < MAX_IMAGES;

	return (
		<>
			<div className={css(styleSheet.mediaHeader)}>
				<span className={`media-section-label ${css(styleSheet.mediaLabel)}`}>{label}</span>
			</div>
			{hasMedia || isBlockedByOtherTabs ? (
				<MediaGrid
					addMediaDisabledReason={addBlockedReason}
					attachments={attachments}
					label={platform ? `${platform} media` : 'Post media'}
					onAddMedia={offersAdd ? () => handleOpenImageModal('upload') : undefined}
					onCrop={canChange ? handleOpenCrop : undefined}
					onEditWithAI={canChange ? handleOpenAIEdit : undefined}
					onEnlarge={handleEnlarge}
					onRemove={canChange ? onRemoveClicked : undefined}
					onReorder={reordered => setActiveImages(() => reordered, platform)}
					readOnly={readOnly}
				/>
			) : offersAdd ? (
				<AddMediaEmptyState
					title='No media selected'
					subtitle='Add images or videos to display with your post'
					onAddMedia={() => handleOpenImageModal('upload')}
				/>
			) : null}
			{isBlockedByOtherTabs ? <p className={css(styleSheet.addBlockedNote)}>{addBlockedReason}</p> : null}

			<UnifiedImageDialog
				allowAIEdit={canChange}
				showAIGenerateTab={canChange}
				showFreeImagesTab={canChange}
				showImageLibraryTab={canChange}
				offerSaveToLibrary
				open={unifiedDialogOpen}
				onOpenChange={setUnifiedDialogOpen}
				initialTab={unifiedDialogTab}
				initialImage={aiEditTarget?.image}
				onImageSelected={handleUnifiedImageSelected}
				onVideoAttachmentGenerated={handleVideoAttachmentGenerated}
				impersonationContext={impersonationContext}
				destinationPlatform={platform}
				logoOverlay={logoOverlay}
				crop={crop}
				postContent={postContent}
				postId={post?.id}
				getPostIdForVideoUpload={getPostIdForVideoUpload}
				forUserId={videoForUserId}
				showUploadTab={canChange}
				showUploadVideoTab={canChange}
				resizeForSocialMedia={true}
				onValidateSelectedFile={onValidateSelectedFile}
				bulkRebrandingImages={canChange ? attachments : undefined}
				onBulkRebrandingImagesSelected={handleBulkRebrandingImagesSelected}
				maxUploadImages={Math.max(MAX_IMAGES - Math.max(attachments.length, baseMedia.length), 0)}
				allowMultipleUpload={true}
				onMultipleImagesSelected={handleMultipleImagesSelected}
				disabledTabs={disabledDialogTabs.disabledTabs}
				disabledTabsReason={disabledDialogTabs.disabledTabsReason}
			/>
		</>
	);
});

export const SocialMediaImageSearchAndDisplay = inject(ImpersonationContextKey)(SocialMediaImageSearchAndDisplayBase);
